import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { clearCart } from '../cart/cartSlice';
import axios from '../../axiosConfig';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import CustomerFacingSubtotal from '../../components/checkout/CustomerFacingSubtotal';

const OrderConfirmation = () => {
  // Get currency formatting from storefront context
  const { formatPrice } = useStorefrontCurrency();

  // Currency formatter helper that uses the context
  const currency = {
    format: (amount) => formatPrice(amount)
  };
  console.log('OrderConfirmation component loaded');
  console.log('Current URL:', window.location.href);
  console.log('Current pathname:', window.location.pathname);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerToken, setCustomerToken] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Check if coming from payment redirect with reference
        const paymentReference = searchParams.get('reference');

        let orderId = location.state?.orderId;
        let storeSlug = location.state?.storeSlug;
        const suppliedOrder = location.state?.order || null;

        console.log('OrderConfirmation: Payment reference from URL:', paymentReference);
        console.log('OrderConfirmation: Received state:', { orderId, storeSlug });

        // If payment reference exists, verify payment first
        if (paymentReference) {
          // Get transaction_id for Flutterwave verification (Flutterwave adds this to redirect URL)
          const transactionId = searchParams.get('transaction_id');
          console.log('OrderConfirmation: Verifying payment with reference:', paymentReference);
          console.log('OrderConfirmation: Flutterwave transaction_id (if any):', transactionId);
          try {
            const verifyResponse = await axios.post('/checkout/verify-payment', {
              reference: paymentReference,
              transaction_id: transactionId // Pass for Flutterwave verification
            });

            console.log('OrderConfirmation: Payment verification response:', verifyResponse.data);

            if (verifyResponse.data.success) {
              orderId = verifyResponse.data.order.id;
              storeSlug = verifyResponse.data.store?.slug;

              // Store customer token if provided (for View Order button navigation)
              // Token is NOT stored in localStorage - it's passed via URL for one-time auth redirect
              // The customer app will exchange this token for an HttpOnly cookie
              if (verifyResponse.data.customerToken) {
                setCustomerToken(verifyResponse.data.customerToken);
                console.log('OrderConfirmation: Customer token received for redirect');
              }

              // Set order data from verification response (no need for second request)
              console.log('OrderConfirmation: Order data received from payment verification:', verifyResponse.data.order);
              setOrder(verifyResponse.data.order);

              // Use store from verification response directly - skip second API call
              if (verifyResponse.data.store) {
                setStore(verifyResponse.data.store);
              }
            } else {
              setError('Payment verification failed. Please contact support.');
              setLoading(false);
              return;
            }
          } catch (verifyError) {
            console.error('OrderConfirmation: Payment verification error:', verifyError);
            setError('Failed to verify payment. Please contact support with reference: ' + paymentReference);
            setLoading(false);
            return;
          }
        }

        if (!orderId || !storeSlug) {
          console.log('OrderConfirmation: Missing orderId or storeSlug, redirecting to home');
          navigate('/');
          return;
        }

        // COD does not have a payment-verification callback. Checkout passes
        // the server-created order summary directly so this page can show an
        // awaiting-merchant-confirmation receipt without pretending payment
        // was successful.
        if (!paymentReference && suppliedOrder) {
          setOrder(suppliedOrder);
        }

        // Clear cart after successful order confirmation
        dispatch(clearCart());
        console.log('OrderConfirmation: Cart cleared after successful order load');

        // Only fetch store if not already set from verification response (non-payment-redirect case)
        // When coming from payment redirect, store data is already set above from verifyResponse.data.store
        if (!paymentReference && !store && (storeSlug || location.state?.storeSlug)) {
          const slugToFetch = storeSlug || location.state?.storeSlug;
          try {
            console.log('OrderConfirmation: Fetching store details for:', slugToFetch);
            const storeResponse = await axios.get(`/stores/slug/${slugToFetch}`);
            console.log('OrderConfirmation: Store data received:', storeResponse.data);
            setStore(storeResponse.data);
          } catch (storeError) {
            console.error('OrderConfirmation: Failed to fetch store:', storeError);
          }
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [location.state, searchParams, navigate, dispatch]);

  if (loading) {
    console.log('OrderConfirmation: Showing loading state');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {searchParams.get('reference') ? 'Verifying your payment...' : 'Loading your order details...'}
          </p>
          <p className="mt-2 text-sm text-gray-500">URL: {window.location.href}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="w-full">
            Return Home
          </Button>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h1>
          <p className="text-gray-600 mb-6">We couldn't find your order details.</p>
          <Button onClick={() => navigate('/')} className="w-full">
            Return Home
          </Button>
        </Card>
      </div>
    );
  }

  const manualTransfer = location.state?.manualTransfer || null;
  const isCodOrder = order.paymentMethod?.toLowerCase() === 'cod';
  const isManualTransferOrder = order.paymentMethod?.toLowerCase() === 'bank_transfer' && Boolean(manualTransfer?.virtualTerminal);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  // Debug log right before render
  console.log('OrderConfirmation: About to render. Store state:', store);
  console.log('OrderConfirmation: Store logo value:', store?.logo);
  console.log('OrderConfirmation: Should show logo?', !!store?.logo);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      {/* Receipt Box */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        fontFamily: 'monospace',
        fontSize: '14px',
        lineHeight: '1.4'
      }}>
        {/* Header - Centered */}
        <div style={{
          textAlign: 'center',
          padding: '20px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {/* Store Logo */}
          {(() => {
            console.log('OrderConfirmation: Logo render check - store?.logo:', store?.logo);
            return store?.logo ? (
              <div style={{ marginBottom: '12px' }}>
                <img
                  src={store.logo}
                  alt={store.name}
                  style={{
                    maxWidth: '120px',
                    maxHeight: '60px',
                    objectFit: 'contain',
                    margin: '0 auto'
                  }}
                  onLoad={() => console.log('OrderConfirmation: Logo loaded successfully!')}
                  onError={(e) => {
                    console.error('OrderConfirmation: Logo failed to load:', store.logo);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              (() => {
                console.log('OrderConfirmation: No logo to display');
                return null;
              })()
            );
          })()}
          <h1 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            color: '#000'
          }}>
            {store?.name || 'STORE'}
          </h1>
          <p style={{
            fontSize: '12px',
            color: '#6b7280',
            margin: '0'
          }}>
            {formatDate(order.placedAt)}
          </p>
        </div>

        {/* Content - All Left Aligned */}
        <div style={{ padding: '20px' }}>
          {/* Order Info */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>
              {isCodOrder
                ? 'Order sent to merchant'
                : isManualTransferOrder
                  ? 'Complete your bank transfer'
                  : 'Order Confirmation'}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '0' }}>
              Ref: #{order.id.slice(-8).toUpperCase()}
            </p>
          </div>

          {isCodOrder && (
            <div style={{ marginBottom: '20px', padding: '12px', borderRadius: '8px', background: '#ecfdf3', color: '#027a48', fontSize: '12px', lineHeight: 1.5 }}>
              The merchant will confirm your order before arranging delivery. Payment is due when your order is delivered.
            </div>
          )}

          {isManualTransferOrder && (
            <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '10px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a' }}>
              <p style={{ margin: '0 0 12px', fontSize: '13px', lineHeight: 1.5 }}>
                Transfer the exact amount below. Your order is confirmed after the merchant receives payment.
              </p>
              <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}><span>Bank</span><strong>{manualTransfer.virtualTerminal.bank}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}><span>Account number</span><strong>{manualTransfer.virtualTerminal.accountNumber}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}><span>Account name</span><strong>{manualTransfer.virtualTerminal.accountName}</strong></div>
              </div>
            </div>
          )}

          {/* Items */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontWeight: 'bold', margin: '0 0 12px 0' }}>Items:</p>
            {(Array.isArray(order.items) ? order.items : []).map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                paddingBottom: '8px',
                borderBottom: '1px solid #f3f4f6'
              }}>
                <div>
                  <div style={{ fontWeight: '500' }}>{item.name}</div>
                  {item.qty > 1 && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      Qty: {item.qty}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold' }}>
                    {currency.format(item.price * item.qty)}
                  </div>
                  {item.qty > 1 && (
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      @{currency.format(item.price)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div style={{ marginBottom: '20px' }}>
            <CustomerFacingSubtotal
              subtotal={order.subtotal}
              processingFee={
                order.serviceCharge > 0
                  && String(order.serviceChargeWhoPays || 'customer').toLowerCase() === 'customer'
                  ? order.serviceCharge
                  : 0
              }
              formatCurrency={currency.format}
              compact
              rowStyle={{ marginBottom: '4px' }}
              labelStyle={{ color: '#111827', fontWeight: 400 }}
              valueStyle={{ color: '#111827', fontWeight: 400 }}
              subLabel={false}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>{(() => {
                const sub = Number(order.subtotal) || 0;
                const tax = Number(order.taxTotal) || 0;
                if (sub > 0 && tax > 0) {
                  const rate = Math.round((tax / sub) * 1000) / 10; // 1 decimal
                  return `Tax (${rate}%):`;
                }
                return 'Tax:';
              })()}</span>
              <span>{currency.format(order.taxTotal)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>Shipping:</span>
              <span>{currency.format(order.shippingFee)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold',
              fontSize: '16px',
              paddingTop: '8px',
              borderTop: '2px solid #000'
            }}>
              <span>TOTAL:</span>
              <span>{currency.format(order.total)}</span>
            </div>
          </div>

          {/* Details */}
          <div style={{ marginBottom: '20px', fontSize: '12px', color: '#6b7280' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>Customer:</span>
              <span>
                {order.customerInfo?.firstName && order.customerInfo?.lastName
                  ? `${order.customerInfo.firstName} ${order.customerInfo.lastName}`
                  : order.metadata?.customerName || order.customerInfo?.email || '—'}
              </span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '4px'
            }}>
              <span>Payment:</span>
              <span>{
                order.paymentMethod?.toLowerCase() === 'paystack' ? 'Card (Paystack)' :
                order.paymentMethod?.toLowerCase() === 'flutterwave' ? 'Card (Flutterwave)' :
                order.paymentMethod?.toLowerCase() === 'bank_transfer' ? 'Bank Transfer' :
                order.paymentMethod?.toLowerCase() === 'cod' ? 'Cash on Delivery' :
                order.paymentMethod || 'Card'
              }</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>Status:</span>
              <span>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
            </div>
            {/* Delivery Method */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '4px'
            }}>
              <span>Delivery:</span>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                {isCodOrder ? 'Merchant will confirm' : order.metadata?.deliveryMethod === 'CHOWDECK' ? (
                  <>
                    <span style={{
                      background: '#f97316',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: '600',
                      padding: '1px 4px',
                      borderRadius: '3px',
                      textTransform: 'uppercase'
                    }}>EXPRESS</span>
                    Chowdeck
                  </>
                ) : (
                  'Standard'
                )}
              </span>
            </div>
          </div>

          {/* Chowdeck Delivery Note */}
          {order.metadata?.deliveryMethod === 'CHOWDECK' && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              background: '#fff7ed',
              borderRadius: '6px',
              border: '1px solid #fed7aa'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px'
              }}>
                <span style={{
                  fontSize: '16px'
                }}>🚴</span>
                <span style={{
                  fontWeight: '600',
                  color: '#c2410c',
                  fontSize: '13px'
                }}>
                  Chowdeck Express Delivery
                </span>
              </div>
              <p style={{
                fontSize: '11px',
                color: '#9a3412',
                margin: '0',
                lineHeight: '1.4'
              }}>
                Your order will be delivered via Chowdeck. You'll receive a notification when your rider is on the way.
              </p>
            </div>
          )}

          {/* Thank You - Centered */}
          <div style={{
            textAlign: 'center',
            padding: '16px 0',
            borderTop: '1px solid #e5e7eb'
          }}>
            <p style={{
              fontWeight: 'bold',
              fontSize: '16px',
              margin: '0 0 8px 0'
            }}>
              Thank You!
            </p>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              margin: '0'
            }}>
              Please keep this receipt
            </p>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: '8px'
        }}>
          <button
            onClick={() => {
              // For guests, stay on order confirmation page (they're already viewing their order)
              // For logged-in customers, redirect to customer app to view order
              if (customerToken) {
                const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';
                const customerBase = process.env.REACT_APP_CUSTOMER_BASE_URL || `https://my.${platformDomain}`;
                window.location.href = `${customerBase}/auth?token=${customerToken}&redirect=${encodeURIComponent('/orders')}`;
              } else {
                // Guest checkout - just reload the confirmation page or show a message
                alert('Your order details are shown above. To track your order, please create an account or login.');
              }
            }}
            style={{
              flex: 1,
              padding: '10px 20px',
              background: '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1f2937';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#111827';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            View Order
          </button>
          <button
            onClick={() => {
              // Check if we're on a subdomain storefront or path-based
              const hostname = window.location.hostname;
              const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'localhost';
              const isSubdomain = hostname.includes('.') &&
                                 hostname !== platformDomain &&
                                 !hostname.startsWith('www.');

              // If subdomain, just navigate to homepage
              // If path-based, navigate to /s/slug
              if (isSubdomain) {
                navigate('/');
              } else {
                navigate(`/s/${store?.slug || 'store'}`);
              }
            }}
            style={{
              flex: 1,
              padding: '10px 20px',
              background: 'transparent',
              color: '#111827',
              border: '2px solid #111827',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#111827';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#111827';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
